import { Injectable } from '@angular/core';
import type { Row, Worksheet } from 'exceljs';
import type { IncomeItem } from './income.service';

export interface IncomeExportFilters {
  dateFrom: string;
  dateTo: string;
  paymentStage: string;
  paymentMethod: string;
  searchTerm: string;
}

@Injectable({ providedIn: 'root' })
export class IncomeExportService {
  async export(items: IncomeItem[], filters: IncomeExportFilters): Promise<void> {
    const excelJsModule = (await import('exceljs')) as unknown as {
      default: typeof import('exceljs');
    };
    const workbook = new excelJsModule.default.Workbook();
    workbook.creator = 'All To Trips';
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet('รายการรายรับ', {
      properties: { defaultRowHeight: 20 },
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    });

    this.configureColumns(worksheet);
    this.addReportHeader(worksheet, filters, items.length);
    this.addTable(worksheet, items);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `รายการรายรับ-${filters.dateFrom || 'all'}-${filters.dateTo || 'all'}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  private configureColumns(worksheet: Worksheet): void {
    const widths = [8, 14, 24, 22, 20, 36, 28, 18, 20, 24, 18, 20, 20, 20, 26, 16, 14];
    widths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
  }

  private addReportHeader(
    worksheet: Worksheet,
    filters: IncomeExportFilters,
    itemCount: number,
  ): void {
    worksheet.mergeCells('A1:Q1');
    const title = worksheet.getCell('A1');
    title.value = `รายงานรายการรายรับ${filters.dateFrom && filters.dateTo ? ` (${filters.dateFrom} ถึง ${filters.dateTo})` : ''}`;
    title.font = { bold: true, size: 16, color: { argb: 'FF0F172A' } };
    title.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 28;

    const queryRows: Array<[string, string | number]> = [
      ['วันที่เริ่มต้น', filters.dateFrom || 'ทั้งหมด'],
      ['วันที่สิ้นสุด', filters.dateTo || 'ทั้งหมด'],
      ['งวดการชำระ', filters.paymentStage],
      ['ช่องทางรับชำระ', filters.paymentMethod],
      ['คำค้นหา', filters.searchTerm || 'ไม่ได้ระบุ'],
      ['จำนวนรายการ', itemCount],
    ];
    for (let index = 0; index < queryRows.length; index += 2) {
      const leftQuery = queryRows[index];
      const rightQuery = queryRows[index + 1];
      const row = worksheet.addRow([]);

      row.getCell(6).value = leftQuery[0];
      row.getCell(7).value = leftQuery[1];
      if (rightQuery) {
        row.getCell(10).value = rightQuery[0];
        row.getCell(11).value = rightQuery[1];
      }

      [6, 10].forEach((column) => {
        const cell = row.getCell(column);
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
      [7, 11].forEach((column) => {
        const cell = row.getCell(column);
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
    }
    worksheet.addRow([]);
  }

  private addTable(worksheet: Worksheet, items: IncomeItem[]): Row {
    const headerRow = worksheet.addRow([
      'ลำดับ',
      'วันที่รับชำระ',
      'เลขรับชำระ',
      'เลข Booking',
      'รหัสทัวร์',
      'ชื่อทัวร์',
      'ชื่อผู้ชำระ',
      'งวดการชำระ',
      'ช่องทางรับชำระ',
      'ผู้รับภาระค่าธรรมเนียม',
      'ค่าธรรมเนียม (%)',
      'ยอดรายการ',
      'ยอดรับจากลูกค้า',
      'ยอดหัก ณ ที่จ่าย',
      'ผู้ตรวจสอบ',
      'วันที่ตรวจสอบ',
      'เวลาตรวจสอบ',
    ]);
    this.styleHeaderRow(headerRow, 17);

    items.forEach((item, index) => {
      const verifiedDate = item.verifiedAt ? this.formatThaiDate(item.verifiedAt) : '';
      const verifiedTime = item.verifiedAt ? this.formatThaiTime(item.verifiedAt) : '';
      const row = worksheet.addRow([
        index + 1,
        this.toExcelCalendarDate(item.paymentDate),
        item.paymentNo,
        item.bookingNo,
        item.tourCode,
        item.tourName,
        item.payerName,
        this.paymentStageLabel(item.paymentStage),
        this.paymentMethodLabel(item.paymentMethod),
        item.paymentMethod === 'CREDIT_CARD'
          ? this.feeResponsibilityLabel(item.creditCardFeeResponsibility)
          : '',
        item.paymentMethod === 'CREDIT_CARD' ? item.creditCardFeePercent : null,
        item.amount,
        item.customerPaidAmount,
        item.withholdingTaxAmount,
        item.verifiedByName === '-' ? '' : item.verifiedByName,
        verifiedDate,
        verifiedTime,
      ]);
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = this.thinBorder();
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      row.getCell(2).numFmt = 'dd/mm/yyyy';
      row.getCell(11).numFmt = '0"%"';
      [12, 13, 14].forEach((column) => {
        row.getCell(column).numFmt = '#,##0.00';
      });
    });

    const amountTotal = items.reduce((total, item) => total + item.amount, 0);
    const customerPaidTotal = items.reduce((total, item) => total + item.customerPaidAmount, 0);
    const withholdingTaxTotal = items.reduce(
      (total, item) => total + item.withholdingTaxAmount,
      0,
    );
    const totalRow = worksheet.addRow([
      `รวมทั้งหมด (${items.length} รายการ)`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      amountTotal,
      customerPaidTotal,
      withholdingTaxTotal,
      '',
      '',
      '',
    ]);
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.border = this.thinBorder();
    });
    [12, 13, 14].forEach((column) => {
      totalRow.getCell(column).numFmt = '#,##0.00';
    });

    return headerRow;
  }

  private styleHeaderRow(row: Row, columnCount: number): void {
    row.height = 30;
    for (let column = 1; column <= columnCount; column += 1) {
      const cell = row.getCell(column);
      cell.font = { bold: true, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = this.thinBorder();
    }
  }

  private thinBorder() {
    const side = { style: 'thin' as const, color: { argb: 'FFE2E8F0' } };
    return { top: side, left: side, bottom: side, right: side };
  }

  private toExcelCalendarDate(value: string): Date {
    const date = new Date(value);
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  private formatThaiDate(value: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(value));
  }

  private formatThaiTime(value: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Bangkok',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(value));
  }

  private paymentStageLabel(stage: IncomeItem['paymentStage']): string {
    return {
      BOOKING: 'ค่าจอง',
      DEPOSIT: 'เงินมัดจำ',
      BALANCE: 'ยอดคงเหลือ',
      FULL_PAYMENT: 'ชำระเต็มจำนวน',
    }[stage];
  }

  private paymentMethodLabel(method: IncomeItem['paymentMethod']): string {
    return {
      CASH: 'เงินสด',
      BANK_TRANSFER: 'โอนผ่านธนาคาร',
      CREDIT_CARD: 'บัตรเครดิต',
    }[method];
  }

  private feeResponsibilityLabel(
    responsibility: IncomeItem['creditCardFeeResponsibility'],
  ): string {
    if (responsibility === 'CUSTOMER') return 'ลูกค้าชำระ';
    if (responsibility === 'COMPANY') return 'บริษัทรับภาระ';
    return 'ไม่มีค่าธรรมเนียม';
  }
}
